import React, { useState } from "react"
import moment from "moment"
import { Box, Divider, useTheme, useMediaQuery } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { Author, AuthorTools } from "../Author"
import { FormatDate, FormatDateTimeCompact, FormatTime } from "../Format"
import TimeAgo from "../TimeAgo"
import { useJobUtils } from "../../utils"
import { ExpansionPanel, InfoSection, Avatar } from ".."
import { CopyLinkButton, DownloadExportJobButton } from "../Buttons"
import { Icon } from "../Icon"
import { ProcessAssignees } from "./ProcessAssignees"
import { ColumnBox, PaperBox, RowBox } from "../Boxes"
import { useAuth } from "../../services"
import { Assignees } from "../Assignees"

const useStyles = makeStyles((theme) => ({
  infoLight: {
    color: theme.palette.text.light,
  },
  expansionRoot: {},
  expansionIcon: {
    margin: `0 ${theme.spacing(3)}px 0 13px`,
  },
  expansionSummary: {
    minHeight: 54,
  },
  expansionDetail: {},
  assignees: {
    width: "100%",
  },
}))

const JobMoreInformation = ({ job, onDelete, exportPdf = true }) => {
  const theme = useTheme()
  const { getRepeatDisplayText, isJobAfterToday } = useJobUtils()
  const { hasPermission } = useAuth()
  const classes = useStyles()
  const expansionClasses = {
    root: classes.expansionRoot,
    icon: classes.expansionIcon,
    summary: classes.expansionSummary,
    detail: classes.expansionDetail,
  }
  const [expand, setExpand] = useState(false)
  const {
    availableFrom,
    dueAt,
    updatedAt,
    status: { completed, completedAt, completedBy },
    author,
  } = job

  const scheduled = isJobAfterToday(availableFrom)
  const canUpdate = hasPermission("job_update_all")
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const isProcess = Boolean(job.process)

  const handleDelete = () => {
    onDelete && onDelete()
  }

  return (
    <ExpansionPanel
      title="More information"
      expanded={expand}
      onChange={() => setExpand(!expand)}
      icon={<Icon name="information" />}
      classes={expansionClasses}
    >
      <ColumnBox flexGrow={1}>
        <PaperBox fullWidth>
          <InfoSection variant="info">
            <Author author={author} prefix="Created by" className={classes.infoLight} />
            <Box ml="auto" display="flex" flexDirection="row">
              {exportPdf && <DownloadExportJobButton variant="icon" job={job} />}
              <CopyLinkButton />
              {canUpdate && (
                <AuthorTools
                  item={job}
                  author={author.id}
                  admins={{ delete: "job_update_all" }}
                  subject="job (this instance only)"
                  onDelete={handleDelete}
                  disabled={scheduled}
                />
              )}
            </Box>
          </InfoSection>
          <Divider />
          <InfoSection
            title="Available from"
            value={
              <Box display="flex" flexDirection={xs ? "column" : "row"}>
                <FormatDate value={availableFrom} />
                {moment(availableFrom).isAfter(moment(availableFrom).startOf("day")) && (
                  <Box ml={0.5}>
                    (from <FormatTime value={availableFrom} />)
                  </Box>
                )}
              </Box>
            }
          />
          {dueAt && (
            <>
              <Divider />
              <InfoSection
                title="Due at"
                value={
                  <Box display="flex" flexDirection="row">
                    <FormatTime value={dueAt} showOverdue={!completed} overdueSuffix="(Overdue)" />
                  </Box>
                }
              />
            </>
          )}
          {isProcess && (
            <>
              <Divider />
              <InfoSection title="Repeats" value={getRepeatDisplayText(job)} />
            </>
          )}
          {updatedAt && (
            <>
              <Divider />
              <InfoSection title="Last updated" value={<TimeAgo date={updatedAt} />} />
            </>
          )}
          {completed && (
            <>
              <Divider />
              <InfoSection
                title="Completed"
                value={
                  <RowBox ml="auto" mr={1}>
                    <Box mr={0.5} className={classes.completedByDate}>
                      <FormatDateTimeCompact value={completedAt} />
                    </Box>
                    {completedBy && <Box mr={1}>by {completedBy.fullName}</Box>}
                    {completedBy && <Avatar {...completedBy} className={classes.completedByAvatar} />}
                  </RowBox>
                }
              />
            </>
          )}
          {!canUpdate && (
            <>
              <Divider />
              <InfoSection
                title="Assignees"
                value={<Assignees {...{ users: job.users, groups: job.groups, limit: 2 }} />}
              />
            </>
          )}
        </PaperBox>
        {canUpdate && (
          <Box mt={3} className={classes.assignees}>
            <ProcessAssignees job={job} disabled={scheduled} />
          </Box>
        )}
      </ColumnBox>
    </ExpansionPanel>
  )
}

export { JobMoreInformation }
